import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchPolicyDocument, addPolicy, updatePolicy } from "../../redux/actions/grievanceActions1";
import { fileToBase64, validatePolicyFile } from "../../utils/fileUploadUtils";
import FilePreviewModal from "../common/FilePreviewModal";

const PolicyModal = ({ onClose, show, mode = "add", policyDetails = null }) => {
  const dispatch = useDispatch();
  
  // Separate state for each field
  const [title, setTitle] = useState("");
  const [document, setDocument] = useState(null);
  const [isActive, setIsActive] = useState(true);

  // Error state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Use separate loading flags for view and download flows
  const [isViewingDocument, setIsViewingDocument] = useState(false);
  const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState("");
  const [previewFileName, setPreviewFileName] = useState("");

  // Initialize form with policy details when component mounts or policyDetails changes
  useEffect(() => {
    if (policyDetails && (mode === "update" || mode === "view")) {
      setTitle(policyDetails.title || "");
      setDocument(null); // Don't pre-fill document for security
      setIsActive(policyDetails.status === "active");
    } else if (mode === "add") {
      resetForm();
    }
  }, [policyDetails, mode]);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!title.trim()) newErrors.title = "Policy title is required";
    if (mode === "add" && !document) newErrors.document = "Document is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (mode === "view") {
      onClose();
      return;
    }

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      let attachmentBase64 = null;
      
      // Convert file to base64 if document is selected
      if (document) {
        try {
          await validatePolicyFile(document);
          attachmentBase64 = await fileToBase64(document);
        } catch (fileError) {
          alert(fileError.message);
          setIsSubmitting(false);
          return;
        }
      }

      if (mode === "add") {
        const policyData = {
          title: title.trim(),
          attachment: attachmentBase64,
          is_active: isActive ? "1" : "0"
        };
        
        await dispatch(addPolicy(policyData));
        alert("Policy created successfully!");
      } else if (mode === "update") {
        const policyData = {
          title: title.trim(),
          is_active: isActive ? "1" : "0"
        };
        // Only send attachment if a new document is selected
        if (attachmentBase64) {
          policyData.attachment = attachmentBase64;
        }
        
        await dispatch(updatePolicy(policyDetails.id, policyData));
        alert("Policy updated successfully!");
      }

      resetForm();
      onClose(true); // Pass true to indicate successful operation
    } catch (error) {
      console.error(`Error ${mode === "add" ? "creating" : "updating"} policy:`, error);
      alert(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDocument(null);
    setIsActive(true);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose(false); // Pass false to indicate no refresh needed
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        await validatePolicyFile(file);
        setDocument(file);
        // Clear any previous document error
        if (errors.document) {
          setErrors(prev => ({ ...prev, document: "" }));
        }
      } catch (error) {
        alert(error.message);
        e.target.value = ''; // Clear the file input
        setDocument(null);
      }
    }
  };

  const handleViewDocument = async () => {
    if (!policyDetails?.id) return;
    
    setIsViewingDocument(true);
    try {
      const base64Data = await dispatch(fetchPolicyDocument(policyDetails.id));
      if (base64Data) {
        // Use FilePreviewModal for robust preview (pdf/image/etc)
        setPreviewAttachment(base64Data);
        // Ensure we pass a filename with extension for FilePreviewModal to infer type
        const name = policyDetails.document || `policy_${policyDetails.id}.pdf`;
        setPreviewFileName(name);
        setIsPreviewOpen(true);
      } else {
        alert("Document not found");
      }
    } catch (error) {
      console.error("Error viewing document:", error);
      alert("Error viewing document. Please try again.");
    } finally {
      setIsViewingDocument(false);
    }
  };

  const getModalTitle = () => {
    switch (mode) {
      case "add":
        return "Add New Policy";
      case "update":
        return "Update Policy";
      case "view":
        return "View Policy Details";
      default:
        return "Policy";
    }
  };

  const getModalIcon = () => {
    switch (mode) {
      case "add":
        return "bi-file-earmark-plus";
      case "update":
        return "bi-pencil-square";
      case "view":
        return "bi-eye";
      default:
        return "bi-file-earmark";
    }
  };

  const getSubmitButtonText = () => {
    switch (mode) {
      case "add":
        return "Create Policy";
      case "update":
        return "Update Policy";
      case "view":
        return "Close";
      default:
        return "Submit";
    }
  };

  const getSubmitButtonIcon = () => {
    switch (mode) {
      case "add":
        return "bi-plus-circle";
      case "update":
        return "bi-check-circle";
      case "view":
        return "bi-x-circle";
      default:
        return "bi-check";
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block", background: "rgba(0, 0, 0, 0.5)" }}
        tabIndex={-1}
        role="dialog"
        aria-labelledby="PolicyModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
          <div style={{ backgroundColor: "#fff" }} className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title fw-bold text-primary"
                id="PolicyModalLabel"
              >
                <i className={`bi ${getModalIcon()} me-2`}></i>
                {getModalTitle()}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body">
              <form>
                {/* Policy Title */}
                <div className="mb-3">
                  <label htmlFor="policy-title" className="form-label">
                    Policy Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.title ? "is-invalid" : ""
                    }`}
                    id="policy-title"
                    placeholder="Enter policy title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={mode === "view"}
                  />
                  {errors.title && (
                    <div className="invalid-feedback">{errors.title}</div>
                  )}
                </div>

                {/* Document Upload */}
                <div className="mb-3">
                  <label htmlFor="policy-document" className="form-label">
                    Document {mode === "add" && <span className="text-danger">*</span>}
                  </label>
                  
                  {/* Current Document Display (for view/update modes when no new file selected) */}
                  {mode !== "add" && policyDetails?.document && !document && (
                    <div className="mb-3 p-3 bg-light rounded-3 border">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-file-earmark-pdf me-2 text-danger fs-4"></i>
                          <div>
                            <div className="fw-semibold">Current: {policyDetails.document}</div>
                            <small className="text-muted">
                              <i className="bi bi-file-earmark me-1"></i>
                              Existing document
                            </small>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary rounded-pill"
                            title="View Current Document"
                            onClick={handleViewDocument}
                            disabled={isViewingDocument}
                          >
                            {isViewingDocument ? (
                              <div className="spinner-border spinner-border-sm me-1" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            ) : (
                              <i className="bi bi-eye me-1"></i>
                            )}
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* File Upload Input */}
                  <input
                    type="file"
                    className={`form-control ${
                      errors.document ? "is-invalid" : ""
                    }`}
                    id="policy-document"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileChange}
                    disabled={mode === "view"}
                    max="10485760"
                  />
                  
                  {/* New File Preview (for both add and update modes) */}
                  {document && (
                    <div className="mt-3 p-3 bg-light rounded-3 border">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-file-earmark-plus me-2 text-success fs-4"></i>
                          <div>
                            <div className="fw-semibold text-success">
                              {mode === "update" ? "New file: " : "File: "}{document.name}
                            </div>
                            <small className="text-muted">
                              <i className="bi bi-check-circle me-1"></i>
                              {mode === "update" ? "Will replace current document" : "Ready to upload"}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="form-text">
                    {mode === "update" 
                      ? "Upload new document to replace existing one (Supported formats: PDF, DOC, DOCX, TXT)"
                      : "Supported formats: PDF, DOC, DOCX, TXT (Max size: 10MB)"}
                  </div>
                  {errors.document && (
                    <div className="invalid-feedback">{errors.document}</div>
                  )}
                </div>

                {/* Status Toggle */}
                <div className="mb-3">
                  <label className="form-label d-block">
                    Status
                  </label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="policy-status"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      style={{ width: "3rem", height: "1.5rem" }}
                      disabled={mode === "view"}
                    />
                    <label className="form-check-label ms-2" htmlFor="policy-status">
                      <span className={`badge ${isActive ? 'bg-success' : 'bg-warning'} fs-6`}>
                        <i className={`bi ${isActive ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </label>
                  </div>
                  <div className="form-text">
                    Toggle to set policy as Active or Inactive
                  </div>
                </div>
              </form>
            </div>

            <div className="modal-footer">              
              <button
                type="button"
                className="btn btn-secondary rounded-pill px-4"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                <i className="bi bi-x-circle me-2"></i>
                Cancel
              </button>
              
              <button
                type="button"
                className="btn btn-primary rounded-pill px-4 d-flex align-items-center"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">
                        {mode === "add" ? "Creating..." : mode === "update" ? "Updating..." : "Processing..."}
                      </span>
                    </div>
                    {mode === "add" ? "Creating..." : mode === "update" ? "Updating..." : "Processing..."}
                  </>
                ) : (
                  <>
                    <i className={`bi ${getSubmitButtonIcon()} me-2`}></i>
                    {getSubmitButtonText()}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {isPreviewOpen && (
        <FilePreviewModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          attachment={previewAttachment}
          fileTypes={previewFileName}
        />
      )}
    </>
  );
};

export default PolicyModal;
